import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, Button, FormHelperText, IconButton, TextField } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { LoadingButton } from "@mui/lab";
import { Formik } from "formik";
import * as Yup from "yup";
import useMounted from "hooks/useMounted";
import useLoginGuard from "hooks/useLoginGuard";
import { useNavigate } from "react-router";
import PasswordPolicyHint, { combinationPatt } from "./PasswordPolicyHint";
import useAuth from "hooks/useAuth";
import toast from "react-hot-toast";
import useCountdown from "hooks/useCountdown";
import { LoginSetting } from "contexts/LoginSettingsContext";

const ResetFormFormik: FC<{ handleCancel?: () => void; currEmail?: string }> = ({
    handleCancel,
    currEmail
}) => {
    const mounted = useMounted();
    const { settings, saveSettings } = useLoginGuard();
    const { passwordReset, login, passwordRecovery } = useAuth();
    const navigate = useNavigate();

    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showPassword2, setShowPassword2] = useState<boolean>(false);

    const [count, { startCountdown, resetCountdown }] = useCountdown({
        countStart: 60,
        intervalMs: 1000
    });

    useEffect(() => {
        startCountdown();

        if (currEmail && !settings.currEmail) {
            saveSettings((prev: LoginSetting) => {
                return {
                    ...prev,
                    currEmail
                } as LoginSetting;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Formik
                initialValues={{
                    vCode: "",
                    password: "",
                    confirmPassword: "",
                    submit: null
                }}
                validationSchema={Yup.object({
                    vCode: Yup.string().required("Verify code is required"),
                    password: Yup.string()
                        .min(8, { message: "At least 8 characters total." })
                        .max(255)
                        .required("Password is required")
                        .matches(
                            combinationPatt,
                            "Password must contain at least 8 characters, one uppercase, one number and one special case character"
                        ),

                    confirmPassword: Yup.string().oneOf(
                        [Yup.ref("password"), null],
                        "Passwords must match"
                    )
                })}
                onSubmit={async (
                    values,
                    { setErrors, setStatus, setSubmitting }
                ): Promise<void> => {
                    try {
                        if (!Boolean(settings.currEmail)) {
                            toast.error("Invalid email address.");
                            navigate("/login");
                            saveSettings({
                                ...settings,
                                currStep: "LOGIN"
                            });
                            return;
                        }
                        await passwordReset(settings.currEmail, values.vCode, values.password);
                        toast.success("Your password reset successfully and loggin in");

                        const res = await login(settings.currEmail, values.password);
                        // NEW_PASSWORD_REQUIRED
                        if (res === "NEW_PASSWORD_REQUIRED") {
                            saveSettings({
                                ...settings,
                                currStep: "NEW_PASSWORD_REQUIRED"
                            });
                            return;
                        }
                        // MFA_SETUP  need to scan the qrcode
                        if (res === "MFA_SETUP") {
                            saveSettings({
                                ...settings,
                                currStep: "MFA_SETUP"
                            });
                            return;
                        }
                        // SOFTWARE_TOKEN_MFA  enter one time password
                        if (res === "SOFTWARE_TOKEN_MFA") {
                            saveSettings({
                                ...settings,
                                currStep: "SOFTWARE_TOKEN_MFA"
                            });
                            return;
                        }

                        if (mounted.current) {
                            setStatus({ success: true });
                            setSubmitting(false);
                        }
                    } catch (err: any) {
                        console.error(err);
                        toast.error(err.message);
                        if (mounted.current) {
                            setStatus({ success: false });
                            setErrors({ submit: err.message });
                            setSubmitting(false);
                        }
                    }
                }}>
                {({
                    errors,
                    handleChange,
                    handleSubmit,
                    isSubmitting,
                    touched,
                    values
                }): JSX.Element => (
                    <form noValidate onSubmit={handleSubmit}>
                        <input
                            type="text"
                            name="_prevent_auto_complete_name"
                            autoComplete="off"
                            readOnly
                            style={{ display: "none" }}
                        />
                        <input
                            type="password"
                            name="_prevent_auto_complete_pass"
                            autoComplete="new-password"
                            readOnly
                            style={{ display: "none" }}
                        />

                        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, mb: 5 }}>
                            <TextField
                                autoFocus
                                error={Boolean(touched.vCode && errors.vCode)}
                                fullWidth
                                helperText={touched.vCode && errors.vCode}
                                label="Verification code"
                                margin="normal"
                                name="vCode"
                                onChange={handleChange}
                                type="text"
                                value={values.vCode}
                                variant="standard"
                            />
                            <Button
                                disabled={count > 0}
                                size="small"
                                variant="contained"
                                sx={{ position: "relative", top: -10 }}
                                onClick={async () => {
                                    resetCountdown();
                                    await passwordRecovery(settings.currEmail);
                                    toast.success(
                                        "The new verification code has been sent, please check your email."
                                    );
                                }}>
                                {count > 0 ? `${count}s` : "RESEND"}
                            </Button>
                        </Box>
                        <PasswordPolicyHint sx={{ my: 1 }} password={values.password} />
                        <TextField
                            error={Boolean(touched.password && errors.password)}
                            fullWidth
                            helperText={touched.password && errors.password}
                            label="Password"
                            margin="normal"
                            name="password"
                            onChange={handleChange}
                            type={showPassword ? "text" : "password"}
                            value={values.password}
                            variant="standard"
                            autoComplete="new-password"
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        tabIndex={-1}
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowPassword(!showPassword)}>
                                        {showPassword ? (
                                            <VisibilityOff fontSize="small" />
                                        ) : (
                                            <Visibility fontSize="small" />
                                        )}
                                    </IconButton>
                                )
                            }}
                        />
                        <TextField
                            error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                            fullWidth
                            helperText={touched.confirmPassword && errors.confirmPassword}
                            label="Confirm password"
                            margin="normal"
                            name="confirmPassword"
                            onChange={handleChange}
                            type={showPassword2 ? "text" : "password"}
                            value={values.confirmPassword}
                            variant="standard"
                            autoComplete="new-password"
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        tabIndex={-1}
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowPassword2(!showPassword2)}>
                                        {showPassword2 ? (
                                            <VisibilityOff fontSize="small" />
                                        ) : (
                                            <Visibility fontSize="small" />
                                        )}
                                    </IconButton>
                                )
                            }}
                        />
                        {errors.submit && (
                            <Box>
                                <FormHelperText error>{errors.submit}</FormHelperText>
                            </Box>
                        )}
                        <Box
                            sx={{
                                mt: 5,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center"
                            }}>
                            <Button
                                color="primary"
                                onClick={
                                    handleCancel
                                        ? () => {
                                              handleCancel();
                                          }
                                        : () => {
                                              navigate("/login");
                                              saveSettings({
                                                  ...settings,
                                                  currStep: "LOGIN"
                                              });
                                          }
                                }>
                                CANCEL
                            </Button>
                            <LoadingButton
                                color="primary"
                                sx={{ height: 39, fontSize: 14 }}
                                loading={isSubmitting}
                                size="large"
                                type="submit"
                                variant="contained">
                                RESET PASSWORD
                            </LoadingButton>
                        </Box>
                    </form>
                )}
            </Formik>
        </Box>
    );
};

export default ResetFormFormik;
